import moment from 'moment';
import {RawLocation} from 'vue-router';
import {placeWithState} from '@/helpers/stateName.helper';
import {InstitutionTypesVue} from '@/enums/InstitutionTypes.enum';
import {InstitutionStatus} from '@/enums/InstitutionStatus.enum';
import {AcquisitionListStatusEnum} from '@/enums/institutioner/acquisition/AcquisitionListStatus.enum';
import {QuickSchedule} from '@/components/app/search/searchSchedule';
import {PaletteEntity, PaletteHit} from '@/components/app/search/searchEntities';

/** A label / value row of the quick view, optionally linking to another view; `detail` is a short plain text preview below */
export type QuickFact = { label: string; value: string; to?: RawLocation; detail?: string };

/** Compact log entry of the profile (pinned or newest) */
export type QuickLog = {
    id: string;
    icon: string;
    tone: string;
    typeLabel: string;
    /** e.g. "06.10.2026" */
    dateText: string;
    author: string;
    /** Plain text, empty without read access to the payload */
    text: string;
    pinned: boolean;
    /** Profile with the entry focused in the timeline (`?log=<id>`) */
    to: RawLocation | null;
};

/** Additional button of the quick view (e.g. "Standort öffnen") */
export type QuickLink = { label: string; icon: string; to: RawLocation };

/** Additional tag next to the status (e.g. "abgesagt", series of a training) */
export type QuickTag = { text: string; tone: string };

export type QuickContext = {
    facts: QuickFact[];
    tags: QuickTag[];
    links: QuickLink[];
    phones: string[];
    mails: string[];
    /** Courses / mini timetable (parent, student, teacher, institution); null if not allowed or not applicable */
    schedule: QuickSchedule | null;
    /** Pinned entries first, then the newest ones (only with read access to log entries) */
    logs: QuickLog[];
};

export function emptyContext(): QuickContext {
    return {facts: [], tags: [], links: [], phones: [], mails: [], schedule: null, logs: []};
}

/** Readable text of a preview value (string, number, populated document, list) */
export function textOf(value: unknown): string {
    if (value === null || value === undefined) {
        return '';
    }
    if (typeof value === 'string') {
        return value.trim();
    }
    if (typeof value === 'number') {
        return String(value);
    }
    if (Array.isArray(value)) {
        return value.map(textOf).filter((el) => el).join(', ');
    }
    if (typeof value === 'object') {
        const obj = value as Record<string, unknown>;
        if (obj.name) {
            return textOf(obj.name);
        }
        if (obj.firstName || obj.lastName) {
            return `${textOf(obj.firstName)} ${textOf(obj.lastName)}`.trim();
        }
        if (obj.text) {
            return textOf(obj.text);
        }
    }
    return '';
}

/** Id of a populated reference in the preview (object with _id / id) */
export function idOf(value: unknown): string {
    if (typeof value === 'string' && /^[a-f\d]{24}$/i.test(value)) {
        return value;
    }
    if (value && typeof value === 'object') {
        const obj = value as Record<string, unknown>;
        const id = obj._id || obj.id;
        return id ? String(id) : '';
    }
    return '';
}

function asRecord(value: unknown): Record<string, unknown> {
    return value && typeof value === 'object' && !Array.isArray(value) ? value as Record<string, unknown> : {};
}

function asList(value: unknown): unknown[] {
    if (Array.isArray(value)) {
        return value;
    }
    return value === null || value === undefined || value === '' ? [] : [value];
}

function pushFact(facts: QuickFact[], label: string, value: unknown, to?: RawLocation) {
    const text = textOf(value);
    if (text) {
        facts.push({label, value: text, to});
    }
}

function placeOf(preview: Record<string, unknown>): string {
    const postal = textOf(preview.postal || preview.postalCode);
    const city = textOf(preview.city);
    const place = [postal, city].filter((el) => el).join(' ');
    return placeWithState(place, textOf(preview.state));
}

function birthdayText(value: unknown): string {
    const date = typeof value === 'string' || typeof value === 'number' || value instanceof Date ? moment(value) : null;
    if (!date || !date.isValid()) {
        return '';
    }
    return `${date.format('DD.MM.YYYY')} (${moment().diff(date, 'years')} Jahre)`;
}

function enumText<T extends string | number>(value: unknown, items: Array<{ value: T; text: string }>): string {
    const item = items.find((el) => el.value === value || String(el.value) === String(value));
    return item ? item.text : textOf(value);
}

function recordItems<T extends string | number>(record: Record<T, { text: string }>): Array<{ value: T; text: string }> {
    return (Object.keys(record) as T[]).map((key) => ({value: key, text: record[key].text}));
}

/** Phone numbers and mail addresses of the preview, including those of the parents of a student */
export function previewContacts(hit: PaletteHit): { phones: string[]; mails: string[] } {
    const preview = hit.preview || {};
    const phones = [preview.phone1, preview.mobile1, preview.phone, preview.mobile].map(textOf).filter((el) => el);
    const mails = [preview.mail1, preview.mail].map(textOf).filter((el) => el);
    for (const parent of asList(preview.parents)) {
        const obj = asRecord(parent);
        phones.push(...[obj.phone, obj.phone1, obj.mobile1].map(textOf).filter((el) => el));
        mails.push(...[obj.mail, obj.mail1].map(textOf).filter((el) => el));
    }
    return {phones: Array.from(new Set(phones)), mails: Array.from(new Set(mails))};
}

function rolesText(preview: Record<string, unknown>): string {
    const roles: string[] = [];
    if (preview.isEmployee) {
        roles.push('Mitarbeiter:in');
    }
    if (preview.isTeacher) {
        roles.push('Lehrkraft');
    }
    if (preview.isApplicant) {
        roles.push('Bewerber:in');
    }
    return roles.join(', ');
}

function studentFacts(preview: Record<string, unknown>, facts: QuickFact[]) {
    pushFact(facts, 'Geburtstag', birthdayText(preview.birthday));
    pushFact(facts, 'Ort', placeOf(preview));
    pushFact(facts, 'Klasse', preview.grade);
    const institutionId = idOf(preview.institution) || idOf(preview.institutionId);
    pushFact(facts, 'Standort', preview.institution || preview.institutionName, institutionId ? `/institutions/${institutionId}` : undefined);
    for (const parent of asList(preview.parents)) {
        const obj = asRecord(parent);
        const name = textOf(parent);
        const contact = [obj.phone, obj.phone1, obj.mobile1, obj.mail, obj.mail1].map(textOf).filter((el) => el)[0] || '';
        const parentId = idOf(parent);
        pushFact(facts, 'Eltern', [name, contact].filter((el) => el).join(' · '), parentId ? `/parents/${parentId}` : undefined);
    }
}

function institutionFacts(preview: Record<string, unknown>, facts: QuickFact[]) {
    pushFact(facts, 'Art', enumText(preview.type, InstitutionTypesVue));
    pushFact(facts, 'Ort', placeOf(preview));
    pushFact(facts, 'Akquise', enumText(preview.acquisitionStatus, recordItems(AcquisitionListStatusEnum.Record)));
    pushFact(facts, 'Status', enumText(preview.status, recordItems(InstitutionStatus.Record)));
    pushFact(facts, 'Schüler:innen (Schule)', preview.totalAmountPupils);
    pushFact(facts, 'Telefon', preview.phone1);
}

/** Facts that can be shown right away from the preview of the hit */
export function previewFacts(hit: PaletteHit): QuickFact[] {
    const preview = hit.preview || {};
    const facts: QuickFact[] = [];
    switch (hit.entity) {
        case 'students':
            studentFacts(preview, facts);
            break;
        case 'employees':
        case 'teachers':
        case 'applicants':
            pushFact(facts, 'Rolle', rolesText(preview));
            pushFact(facts, 'Fächer', preview.subjects);
            pushFact(facts, 'Ort', placeOf(preview));
            pushFact(facts, 'Telefon', preview.phone1 || preview.mobile1);
            pushFact(facts, 'E-Mail', preview.mail1);
            break;
        case 'parents':
            pushFact(facts, 'Ort', placeOf(preview));
            pushFact(facts, 'Telefon', preview.phone);
            pushFact(facts, 'E-Mail', preview.mail);
            break;
        case 'institutions':
            institutionFacts(preview, facts);
            break;
        case 'courses':
            pushFact(facts, 'Kursnummer', preview.customId || hit.customId);
            break;
        case 'trainings':
            pushFact(facts, 'Termin', preview.when);
            pushFact(facts, 'Ort', preview.venue);
            break;
        case 'products':
            pushFact(facts, 'Name', preview.name);
            pushFact(facts, 'Nummer', preview.customId || hit.customId);
            break;
    }
    return facts;
}

/** Entities whose profile can carry log entries and to-dos */
export const LOGGABLE_ENTITIES: PaletteEntity[] = ['students', 'parents', 'employees', 'teachers', 'applicants', 'institutions'];
